import { useState, useEffect, useRef } from "react";
import "../components/css/style.css";

const faqs = [
    {
        question: "What services does RhythmGrowthLab provide?",
        answer: (
        <>
            <p>We provide end-to-end digital solutions that help brands build, brand, and grow</p>
            <ul>
            <li>Website Development (custom, fast, conversion-focused)</li>
            <li>Mobile App Development (Android & IOS apps)</li>
            <li>Complete Brand Identity (logo, visuals, brand systems)</li>
            <li>Digital Marketing (SEO, content, social presence)</li>
            <li>Performance Marketing (Meta & Google Ads, ROI-driven growth)</li>
            </ul>
        </>
        ),
    },
    {
        question: "What is your pricing model?",
        answer: (
            <>
                <p>Our pricing is flexible and customized based on project scope:</p>
                <ul>
                    <li>Website Development: Starting from ₹5,999</li>
                    <li>Mobile App Development: Custom quotes based on features</li>
                    <li>Branding Packages: Starting from ₹599</li>
                    <li>Digital Marketing: Monthly retainers from ₹1,999</li>
                </ul>
                <p>We offer payment plans and packages tailored to your budget.</p>
            </>
        ),
    },
    {
        question: "What is the typical project timeline?",
        answer: (
            <>
                <p>Timeline varies by project complexity:</p>
                <ul>
                    <li>Website Development: 2-4 weeks</li>
                    <li>Mobile App: 4-8 weeks</li>
                    <li>Branding: 1-2 weeks</li>
                    <li>Marketing Campaigns: Ongoing (results in 1-2 months)</li>
                </ul>
                <p>We provide a detailed timeline during the consultation phase.</p>
            </>
        ),
    },
    {
        question: "Do you provide post-launch support?",
        answer: (
            <>
                <p>Yes! We offer comprehensive post-launch support including:</p>
                <ul>
                    <li>Technical maintenance and updates</li>
                    <li>Bug fixes and performance optimization</li>
                    <li>Content updates and management</li>
                    <li>Monthly monitoring and reporting</li>
                </ul>
            </>
        ),
    },
    {
        question: "How do I get started with RhythmGrowthLab?",
        answer: (
        <>
            <p>Getting started is simple:</p>
            <ol>
            <li>Contact us via website or WhatsApp</li>
            <li>We schedule a free consultation</li>
            <li>Receive a custom proposal and timeline</li>
            <li>Once approved, we begin development</li>
            <li>Receive updates, testing, and revisions</li>
            <li>Final delivery with support options</li>
            </ol>
        </>
        ),
    },
];

export default function FAQSection() {
    const [activeIndex, setActiveIndex] = useState(null);
    const [animatedItems, setAnimatedItems] = useState({});
    
    const sectionRef = useRef(null);
    const leftRef = useRef(null);
    const rightRef = useRef(null);
    const faqItemRefs = useRef([]);

    useEffect(() => {
        const sectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setAnimatedItems(prev => ({ ...prev, section: true }));
                    }
                });
            },
            { threshold: 0.2, rootMargin: "0px" }
        );

        const leftObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setAnimatedItems(prev => ({ ...prev, left: true }));
                    }
                });
            },
            { threshold: 0.3, rootMargin: "0px" }
        );

        const rightObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setAnimatedItems(prev => ({ ...prev, right: true }));
                        
                        if (faqItemRefs.current.length > 0) {
                            faqItemRefs.current.forEach((ref, index) => {
                                setTimeout(() => {
                                    setAnimatedItems(prev => ({ 
                                        ...prev, 
                                        [`faq-${index}`]: true 
                                    }));
                                }, index * 100);
                            });
                        }
                    }
                });
            },
            { threshold: 0.1, rootMargin: "0px" }
        );

        // Start
        if (sectionRef.current) sectionObserver.observe(sectionRef.current);
        if (leftRef.current) leftObserver.observe(leftRef.current);
        if (rightRef.current) rightObserver.observe(rightRef.current);

        // Cleanup
        return () => {
            sectionObserver.disconnect();
            leftObserver.disconnect();
            rightObserver.disconnect();
        };
    }, []);

    const toggleFAQ = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    return (
        <section 
            ref={sectionRef} 
            className={`faq-section ${animatedItems.section ? 'animate-section' : ''}`}
        >
            <div className="faq-container">
                <div 
                    ref={leftRef}
                    className={`faq-left ${animatedItems.left ? 'animate-left' : ''}`}
                >
                    <div className="faq-tag-wrapper">
                        <span className="faq-tag-dot"></span>
                        <p className="faq-tag">FAQs</p>
                    </div>
                    <h2>Got Questions? <span className="gradient-text">We Have Answers!</span></h2>
                    <p className="faq-subtext">
                        Get answers to common questions before starting your journey with us.
                    </p>
                    <div className="faq-stats">
                        <div className="stat-item">
                            <span className="stat-number">100%</span>
                            <span className="stat-label">Satisfaction Rate</span>
                        </div>
                    </div>

                    <div className="faq-contact">
                        <p>Have questions?</p>
                        <a href="https://wa.me/919582795505?text=Hii" className="contact-link">
                            Contact us directly
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path 
                                    d="M5 12H19M19 12L12 5M19 12L12 19" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </a>
                    </div>
                </div>

                <div 
                    ref={rightRef}
                    className="faq-right"
                >
                    {faqs.map((faq, index) => (
                        <div
                            key={index}
                            ref={el => faqItemRefs.current[index] = el}
                            className={`faq-item ${activeIndex === index ? "active" : ""} 
                                ${animatedItems[`faq-${index}`] ? 'animate-faq-item' : ''}`}
                            onClick={() => toggleFAQ(index)}
                        >
                            <div className="faq-question">
                                <span>{faq.question}</span>
                                <div className={`faq-icon ${activeIndex === index ? "rotate" : ""}`}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <path 
                                            d="M6 9L12 15L18 9" 
                                            stroke="currentColor" 
                                            strokeWidth="2" 
                                            strokeLinecap="round" 
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                            </div>
                            <div
                                className="faq-answer"
                                style={{
                                    maxHeight: activeIndex === index ? "500px" : "0",
                                    opacity: activeIndex === index ? "1" : "0",
                                }}
                            >
                                {faq.answer}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}